import { type RuleItemConfig, RulesContent } from '@carboncopyco/editor-ui';
import { compareUrls } from '@carboncopyco/editor-utils';
import { type IDynamicEditData, IDynamicEditType } from '@carboncopyco/types';
import { useBoolean } from 'ahooks';
import { useEditor } from '../../context/EditorSdkProvider';
import { SpotEditsActionDropdown } from './SpotEditsActionDropdown';
import { SpotEditsPreview } from './SpotEditsPreview';
import { SpotEditsSectionHeader } from './SpotEditsSectionHeader';

interface SpotEditsManagerMenuSectionProps {
  ruleItem: RuleItemConfig<IDynamicEditData>;
  pageUrl: string;
  onDelete: (edit: IDynamicEditData) => void;
  onMouseEnter: (edit: IDynamicEditData) => void;
  onMouseLeave: (edit: IDynamicEditData) => void;
  onClick: (edit: IDynamicEditData) => void;
}
const SpotEditsManagerMenuSection = ({
  ruleItem,
  pageUrl,
  onDelete,
  onMouseEnter,
  onMouseLeave,
  onClick,
}: SpotEditsManagerMenuSectionProps) => {
  const edit = ruleItem.rule;
  const isCurrentPageUrl = edit?.isGlobal || compareUrls(pageUrl, edit.url);
  const [isGlobal, setIsGlobal] = useBoolean(edit?.isGlobal ?? false);
  const editor = useEditor();
  const onSetGlobal = (isGlobal: boolean) => {
    editor.changeModificationToBeGlobal(edit, isGlobal);
    setIsGlobal.toggle();
  };

  return (
    <RulesContent.List.Item
      expandable={false}
      id={ruleItem.id}
      onMouseEnter={isCurrentPageUrl ? () => onMouseEnter(edit) : undefined}
      onMouseLeave={isCurrentPageUrl ? () => onMouseLeave(edit) : undefined}
      onClick={() => {
        if (edit.type !== IDynamicEditType.delete) {
          onClick(edit);
        }
      }}
      header={
        <SpotEditsPreview
          edit={edit}
          id={ruleItem.id}
        />
      }
      action={
        <SpotEditsActionDropdown
          isGlobalRule={isGlobal}
          setGlobalRule={onSetGlobal}
          onDelete={() => {
            onDelete(edit);
          }}
        />
      }
    />
  );
};
export const SpotEditsManagerSection = ({
  url,
  edits,
  pageUrl,
  onDelete,
  onMouseEnter,
  onMouseLeave,
  onClick,
}: {
  url: string;
  edits: IDynamicEditData[];
  pageUrl: string;
  onDelete: (edit: IDynamicEditData) => void;
  onMouseEnter: (edit: IDynamicEditData) => void;
  onMouseLeave: (edit: IDynamicEditData) => void;
  onClick: (edit: IDynamicEditData) => void;
}) => {
  return (
    <div className="hidden [&:has(:nth-child(2))]:block">
      <SpotEditsSectionHeader label={url} />
      <RulesContent.List<IDynamicEditData>
        draggable={false}
        rules={edits.map((edit) => ({
          id: edit.timestamp.toString(),
          rule: edit,
        }))}
        renderItem={({
          ruleItem,
        }: {
          ruleItem: RuleItemConfig<IDynamicEditData>;
        }) => (
          <SpotEditsManagerMenuSection
            ruleItem={ruleItem}
            pageUrl={pageUrl}
            onDelete={onDelete}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onClick={onClick}
          />
        )}
      />
    </div>
  );
};
